// client/src/components/ofertas/OfertaForm.js
import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useMutateOfertas } from '../../hooks/useOfertas';
import { useServicos } from '../../hooks/useServicos';
import { useRecursos } from '../../hooks/useRecursos';
import { useItensVenda } from '../../hooks/useItensVenda';
import { useMyAccountDetails, useTenantI18n } from '../../hooks/useAccounts';
import { useUnidadesParaFormulario } from '../../hooks/useUnidades';
import useAlert from '../../hooks/useAlert';
import {
    XCircle, Plus, Trash2, Building, AlertCircle, Package
} from 'lucide-react';
import OfertaImages from './OfertaImages';
import RecorrenciaFields, { RECORRENCIA_PADRAO } from './RecorrenciaFields';
import SearchableSelect from '../shared/SearchableSelect';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const STEP_IDS = ['principal', 'itens', 'precos', 'unidades', 'imagens', 'configuracoes'];
const STEP_LABELS = ['Principal', 'Itens', 'Preços', 'Unidades', 'Imagens', 'Configurações'];

const initialFormData = {
    nome: '', descricao: '', tipo: 'Individual', itens: [],
    tipoDePrecificacao: 'Fixo', valorFixo: '', pacotesDePreco: [],
    visivelParaCliente: true, precoVisivelParaCliente: true,
    destaqueNaVitrine: false, ativo: true, agendavel: false,
    imagensDaGaleria: [], imagens: [], imagemPrincipal: null,
    disponibilidadeGlobal: { tipo: 'todas_unidades', unidadesPermitidas: [] },
    ofertasPorUnidade: [],
};

const normalizarOfertasPorUnidade = (ofertasPorUnidade = []) => ofertasPorUnidade.map(ou => ({
    unidade: ou.unidade?._id || ou.unidade,
    unidadeNome: ou.unidade?.nome || '',
    disponivel: ou.disponivel !== undefined ? ou.disponivel : true,
    precoEspecifico: ou.precoEspecifico ?? '',
    estoqueEspecifico: ou.estoqueEspecifico || 0,
    controlaEstoque: ou.controlaEstoque || false,
}));

const normalizarDisponibilidadeGlobal = (disponibilidadeGlobal) => ({
    tipo: disponibilidadeGlobal?.tipo || 'todas_unidades',
    unidadesPermitidas: (disponibilidadeGlobal?.unidadesPermitidas || []).map(u => u?._id || u),
});

// ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta —
// currency/locale agora vêm do useTenantI18n() abaixo.
const formatCurrency = (value, currency = 'BRL', locale = 'pt-BR') => {
    const numberValue = Number(value);
    if (isNaN(numberValue) || value === '') return '';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(numberValue);
};

const OfertaForm = ({ onCloseForm, onSaveSuccess, current }) => {
    const { createOferta, updateOferta } = useMutateOfertas();
    const { setAlert } = useAlert();
    const [formData, setFormData] = useState(initialFormData);
    // ✅ Unidade de exibição do "intervalo mínimo entre sessões" por pacote
    // (índice -> 'horas' | 'dias'). O valor persistido no backend continua
    // sempre em horas; a unidade é só de UI, convertida na exibição/edição.
    const [intervaloUnidades, setIntervaloUnidades] = useState({});
    const [currentStep, setCurrentStep] = useState(1);
    const [buscaItemTipo, setBuscaItemTipo] = useState('Servico');
    const [itemSelecionadoId, setItemSelecionadoId] = useState('');
    const [imagemPreview, setImagemPreview] = useState('/images/default-oferta.png');

    const { data: accountData } = useMyAccountDetails();
    const accountId = accountData?._id;
    const { currency, locale } = useTenantI18n();

    const { data: unidadesData, isLoading: loadingUnidades } = useUnidadesParaFormulario(accountId);
    const unidades = unidadesData?.data || [];

    const isEditing = !!current?._id;
    const loading = createOferta.isPending || updateOferta.isPending;
    // ✅ "Individual" = oferta de UM item só — ver onChange() e onSubmit() acima.
    const limiteIndividualAtingido = formData.tipo === 'Individual' && Array.isArray(formData.itens) && formData.itens.length >= 1;

    const calcularPrecoItem = useCallback((item, quantidade = 1) => {
        if (!item || typeof item !== 'object') return 0;
        
        switch (buscaItemTipo) {
            case 'ItemVenda':
                const precoBaseProduto = item.unidadesDetalhes?.[0]?.preco ?? item.preco ?? 0;
                return (Number(precoBaseProduto) || 0) * quantidade;
            case 'Servico': 
                return (Number(item.valorFixo) || 0) * quantidade;
            case 'Recurso': 
                return (Number(item.valorPorHora) || 0) * quantidade;
            default: 
                return 0;
        }
    }, [buscaItemTipo]);

    const servicosParams = useMemo(() => ({
        limit: 50,
        ativo: true,
        account: accountId
    }), [accountId]);
    
    const recursosParams = useMemo(() => ({
        limit: 50,
        ativo: true,
        account: accountId
    }), [accountId]);

    const produtosParams = useMemo(() => ({
        limit: 50,
        ativo: true,
        account: accountId
    }), [accountId]);

    const { data: servicosData, isLoading: loadingServicos } = useServicos(servicosParams);
    const { data: recursosData, isLoading: loadingRecursos } = useRecursos(recursosParams);
    const { data: produtosData, isLoading: loadingProdutos } = useItensVenda(produtosParams);

    const opcoesParaBusca = useMemo(() => {
        let dados = [];
        
        switch (buscaItemTipo) {
            case 'Servico':
                dados = servicosData?.data || [];
                break;
            case 'Recurso':
                dados = recursosData?.data || [];
                break;
            case 'ItemVenda':
                dados = produtosData?.data || [];
                break;
            default:
                dados = [];
        }

        const opcoesValidas = dados
            .filter(item => {
                const itemId = item._id || item.id;
                const isValid = item && itemId && item.nome;
                return isValid;
            })
            .map(item => {
                const itemId = item._id || item.id;
                const preco = calcularPrecoItem(item, 1);
                return {
                    value: itemId,
                    label: `${item.nome} - ${formatCurrency(preco, currency, locale)}`,
                    rawItem: item
                };
            });

        return opcoesValidas;
    }, [buscaItemTipo, servicosData, recursosData, produtosData, calcularPrecoItem, currency, locale]);

    const isBuscandoItens = loadingServicos || loadingRecursos || loadingProdutos;

    const itemSelecionado = useMemo(() => {
        if (!itemSelecionadoId) return null;
        return opcoesParaBusca.find(opcao => opcao.value === itemSelecionadoId)?.rawItem || null;
    }, [itemSelecionadoId, opcoesParaBusca]);

    useEffect(() => {
        if (isEditing && current) {
            const data = {
                ...initialFormData, ...current, 
                valorFixo: current.valorFixo ?? '',
                itens: (current.itens || []).map(item => {
                    const valorUnitPopulado = item.itemId ? calcularPrecoItem(item.itemId, 1) : 0;
                    return {
                        tipo: item.tipo, 
                        itemId: item.itemId?._id || item.itemId, 
                        quantidade: item.quantidade || 1,
                        valorUnitario: item.valorUnitario ?? valorUnitPopulado ?? 0,
                        controlaEstoque: item.controlaEstoque ?? (item.tipo === 'ItemVenda' ? item.itemId?.controlaEstoque : false),
                        disponivel: item.disponivel ?? true,
                        itemName: item.itemId?.nome || 'Item não encontrado/carregado'
                    };
                }),
                pacotesDePreco: (current.pacotesDePreco || []).map(p => ({
                    _id: p._id, nome: p.nome || '', sessoes: p.sessoes || 1, valor: p.valor ?? '',
                    validadeMeses: p.validadeMeses ?? 12,
                    regrasUtilizacao: {
                        sessoesSimultaneas: p.regrasUtilizacao?.sessoesSimultaneas ?? 99,
                        intervaloMinimoEntreSessoes: p.regrasUtilizacao?.intervaloMinimoEntreSessoes ?? 0,
                        permiteAgendamentoOnline: p.regrasUtilizacao?.permiteAgendamentoOnline ?? true
                    },
                    recorrencia: p.recorrencia ? { ...RECORRENCIA_PADRAO, ...p.recorrencia } : { ...RECORRENCIA_PADRAO }
                })),
                visivelParaCliente: current.visivelParaCliente ?? true,
                precoVisivelParaCliente: current.precoVisivelParaCliente ?? true,
                destaqueNaVitrine: current.destaqueNaVitrine ?? false,
                ativo: current.ativo ?? true,
                agendavel: current.agendavel ?? false,
                disponibilidadeGlobal: normalizarDisponibilidadeGlobal(current.disponibilidadeGlobal),
                ofertasPorUnidade: normalizarOfertasPorUnidade(current.ofertasPorUnidade),
            };
            setFormData(data);
            setImagemPreview(current.imagemUrl || '/images/default-oferta.png');
            setCurrentStep(1);
        } else {
            setFormData(initialFormData);
            setImagemPreview('/images/default-oferta.png');
            setCurrentStep(1);
        }
    }, [current, isEditing, calcularPrecoItem]);

    const handleItemSelecionado = useCallback((itemId) => {
        setItemSelecionadoId(itemId);
    }, []);

    const handleAdicionarItem = useCallback(() => {
        if (!itemSelecionado) {
            setAlert("Selecione um item válido primeiro.", "warning");
            return;
        }
        
        const currentItems = Array.isArray(formData.itens) ? formData.itens : [];
        const itemJaExiste = currentItems.some(i => i.tipo === buscaItemTipo && i.itemId === itemSelecionado._id);
        
        if (itemJaExiste) { 
            setAlert("Este item já foi adicionado à oferta.", "warning"); 
            return; 
        }

        const valorBase = calcularPrecoItem(itemSelecionado, 1);
        const novoItem = {
            tipo: buscaItemTipo, 
            itemId: itemSelecionado._id || itemSelecionado.id, 
            quantidade: 1, 
            valorUnitario: valorBase,
            controlaEstoque: buscaItemTipo === 'ItemVenda' ? Boolean(itemSelecionado.controlaEstoque) : false,
            disponivel: true, 
            itemName: itemSelecionado.nome
        };

        setFormData(prev => ({ ...prev, itens: [...currentItems, novoItem] }));
        setItemSelecionadoId('');
        setAlert(`${buscaItemTipo} "${itemSelecionado.nome}" adicionado com sucesso!`, 'success', 2000);
    }, [itemSelecionado, buscaItemTipo, formData.itens, setAlert, calcularPrecoItem]);

    const handleImageUpdate = (updatedOferta) => {
        if (!updatedOferta || !updatedOferta._id || !updatedOferta.nome) {
            console.error("handleImageUpdate recebeu um objeto inválido:", updatedOferta);
            setAlert("Erro ao atualizar imagem. O servidor não respondeu corretamente.", "danger");
            return;
        }
        setImagemPreview(updatedOferta.imagemUrl || '/images/default-oferta.png');
        setFormData(prev => ({
            ...prev,
            ...initialFormData,
            ...updatedOferta,
            valorFixo: updatedOferta.valorFixo ?? '',
            itens: (updatedOferta.itens || []).map(item => {
                const valorUnitPopulado = item.itemId ? calcularPrecoItem(item.itemId, 1) : 0;
                return {
                    tipo: item.tipo, 
                    itemId: item.itemId?._id || item.itemId, 
                    quantidade: item.quantidade || 1,
                    valorUnitario: item.valorUnitario ?? valorUnitPopulado ?? 0,
                    controlaEstoque: item.controlaEstoque ?? (item.tipo === 'ItemVenda' ? item.itemId?.controlaEstoque : false),
                    disponivel: item.disponivel ?? true,
                    itemName: item.itemId?.nome || 'Item não encontrado/carregado'
                };
            }),
            pacotesDePreco: (updatedOferta.pacotesDePreco || []).map(p => ({
                _id: p._id, nome: p.nome || '', sessoes: p.sessoes || 1, valor: p.valor ?? '',
                validadeMeses: p.validadeMeses ?? 12,
                regrasUtilizacao: {
                    sessoesSimultaneas: p.regrasUtilizacao?.sessoesSimultaneas ?? 99,
                    intervaloMinimoEntreSessoes: p.regrasUtilizacao?.intervaloMinimoEntreSessoes ?? 0,
                    permiteAgendamentoOnline: p.regrasUtilizacao?.permiteAgendamentoOnline ?? true
                }
            })),
            disponibilidadeGlobal: normalizarDisponibilidadeGlobal(updatedOferta.disponibilidadeGlobal),
            ofertasPorUnidade: normalizarOfertasPorUnidade(updatedOferta.ofertasPorUnidade),
        }));
    };

    const handleDisponibilidadeTipoChange = useCallback((e) => {
        const tipo = e.target.value;
        setFormData(prev => ({
            ...prev,
            disponibilidadeGlobal: { ...prev.disponibilidadeGlobal, tipo }
        }));
    }, []);

    const getConfigUnidade = useCallback((unidadeId) => {
        return formData.ofertasPorUnidade.find(ou => ou.unidade === unidadeId) || {
            unidade: unidadeId, disponivel: true, precoEspecifico: '', estoqueEspecifico: 0, controlaEstoque: false
        };
    }, [formData.ofertasPorUnidade]);

    const handleToggleUnidadePermitida = useCallback((unidadeId) => {
        setFormData(prev => {
            const jaPermitida = prev.disponibilidadeGlobal.unidadesPermitidas.includes(unidadeId);
            const novasPermitidas = jaPermitida
                ? prev.disponibilidadeGlobal.unidadesPermitidas.filter(id => id !== unidadeId)
                : [...prev.disponibilidadeGlobal.unidadesPermitidas, unidadeId];

            let novasOfertasPorUnidade = prev.ofertasPorUnidade;
            if (!jaPermitida && !prev.ofertasPorUnidade.some(ou => ou.unidade === unidadeId)) {
                novasOfertasPorUnidade = [...prev.ofertasPorUnidade, {
                    unidade: unidadeId, disponivel: true, precoEspecifico: '', estoqueEspecifico: 0, controlaEstoque: false
                }];
            }

            return {
                ...prev,
                disponibilidadeGlobal: { ...prev.disponibilidadeGlobal, unidadesPermitidas: novasPermitidas },
                ofertasPorUnidade: novasOfertasPorUnidade
            };
        });
    }, []);

    const handleUnidadeConfigChange = useCallback((unidadeId, field, value) => {
        setFormData(prev => {
            const existe = prev.ofertasPorUnidade.some(ou => ou.unidade === unidadeId);
            const novasOfertasPorUnidade = existe
                ? prev.ofertasPorUnidade.map(ou => ou.unidade === unidadeId ? { ...ou, [field]: value } : ou)
                : [...prev.ofertasPorUnidade, {
                    unidade: unidadeId, disponivel: true, precoEspecifico: '', estoqueEspecifico: 0, controlaEstoque: false, [field]: value
                }];
            return { ...prev, ofertasPorUnidade: novasOfertasPorUnidade };
        });
    }, []);

    const onChange = useCallback(e => {
        const { name, value, type, checked } = e.target;
        const val = (name === 'valorFixo' && value === '') ? '' : (type === 'checkbox' ? checked : value);
        setFormData(prev => {
            const next = { ...prev, [name]: val };
            // ✅ "Tipo da Oferta" (Individual/Pacote/Combo) e "Tipo de Precificação"
            // (Fixo/Por Pacote/Sob Consulta) são campos independentes no schema,
            // mas o filtro da Loja pública só mostra na aba "Pacotes" ofertas com
            // tipo:'Pacote' (ver publicLojaController.js::getLojaPrincipal) —
            // sem essa sincronização, uma oferta criada com "Por Pacote" e
            // tipo:'Individual' (deixado no valor padrão do passo 1) fica
            // invisível na aba certa da loja, sem nenhum aviso.
            if (name === 'tipoDePrecificacao' && val === 'Por Pacote' && prev.tipo === 'Individual') {
                next.tipo = 'Pacote';
            }
            // ✅ "Individual" = oferta de UM item só, precificado sozinho — sem isso
            // "Individual" e "Combo" faziam a mesma coisa (vários itens, um preço
            // combinado). Voltar pra "Individual" com vários itens já adicionados
            // (ex.: usuário tentou Combo, mudou de ideia) mantém só o primeiro.
            if (name === 'tipo' && val === 'Individual' && Array.isArray(prev.itens) && prev.itens.length > 1) {
                next.itens = prev.itens.slice(0, 1);
            }
            return next;
        });
    }, []);

    const handleRemoveItem = useCallback((index) => {
        setFormData(prev => ({ 
            ...prev, 
            itens: (Array.isArray(prev.itens) ? prev.itens : []).filter((_, i) => i !== index) 
        }));
    }, []);

    const handleAddPacote = useCallback(() => {
        setFormData(prev => ({
            ...prev,
            pacotesDePreco: [...(Array.isArray(prev.pacotesDePreco) ? prev.pacotesDePreco : []),
            {
                nome: '', sessoes: 1, valor: '', validadeMeses: 12,
                regrasUtilizacao: { sessoesSimultaneas: 99, intervaloMinimoEntreSessoes: 0, permiteAgendamentoOnline: true },
                recorrencia: { ...RECORRENCIA_PADRAO }
            }]
        }));
    }, []);

    const handleRemovePacote = useCallback((index) => {
        setFormData(prev => ({
            ...prev,
            pacotesDePreco: (Array.isArray(prev.pacotesDePreco) ? prev.pacotesDePreco : []).filter((_, i) => i !== index)
        }));
        setIntervaloUnidades(prev => {
            const next = {};
            Object.entries(prev).forEach(([i, unidade]) => {
                const idx = Number(i);
                if (idx < index) next[idx] = unidade;
                else if (idx > index) next[idx - 1] = unidade;
            });
            return next;
        });
    }, []);

    // ✅ Converte o valor exibido (na unidade escolhida) para horas antes de
    // salvar em regrasUtilizacao.intervaloMinimoEntreSessoes (sempre em horas).
    const handleIntervaloChange = useCallback((index, valorExibido) => {
        const unidade = intervaloUnidades[index] || 'horas';
        const valorEmHoras = unidade === 'dias' ? (Number(valorExibido) || 0) * 24 : (Number(valorExibido) || 0);
        handlePacoteRegraChange(index, 'intervaloMinimoEntreSessoes', valorEmHoras);
    }, [intervaloUnidades]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleIntervaloUnidadeChange = useCallback((index, novaUnidade) => {
        setIntervaloUnidades(prev => ({ ...prev, [index]: novaUnidade }));
    }, []);

    const handlePacoteChange = useCallback((index, field, value) => {
        setFormData(prev => {
            const pacotesAtuais = Array.isArray(prev.pacotesDePreco) ? prev.pacotesDePreco : [];
            const val = (field === 'valor' && value === '') ? '' : (field === 'sessoes' || field === 'validadeMeses' ? (Number(value) || 1) : value);
            return {
                ...prev,
                pacotesDePreco: pacotesAtuais.map((p, i) => i === index ? { ...p, [field]: val } : p)
            };
        });
    }, []);

    const handlePacoteRegraChange = useCallback((index, field, value) => {
        setFormData(prev => {
            const pacotesAtuais = Array.isArray(prev.pacotesDePreco) ? prev.pacotesDePreco : [];
            const val = field === 'permiteAgendamentoOnline' ? value : (Number(value) || 0);
            return {
                ...prev,
                pacotesDePreco: pacotesAtuais.map((p, i) => i === index
                    ? { ...p, regrasUtilizacao: { ...p.regrasUtilizacao, [field]: val } }
                    : p)
            };
        });
    }, []);

    const handlePacoteRecorrenciaChange = useCallback((index, novaRecorrencia) => {
        setFormData(prev => {
            const pacotesAtuais = Array.isArray(prev.pacotesDePreco) ? prev.pacotesDePreco : [];
            return {
                ...prev,
                pacotesDePreco: pacotesAtuais.map((p, i) => i === index ? { ...p, recorrencia: novaRecorrencia } : p)
            };
        });
    }, []);

    const onSubmit = useCallback(e => {
        e.preventDefault();
        if (!formData.nome.trim()) return setAlert('Nome obrigatório.', 'warning');
        const currentItems = Array.isArray(formData.itens) ? formData.itens : [];
        if (currentItems.length === 0) return setAlert('Adicione itens.', 'warning');
        if (formData.tipo === 'Individual' && currentItems.length > 1) {
            return setAlert('Oferta "Individual" permite só um item. Use "Combo" pra combinar vários.', 'warning');
        }
        if (formData.tipoDePrecificacao === 'Fixo' && (formData.valorFixo === '' || formData.valorFixo === null || Number(formData.valorFixo) < 0)) {
            return setAlert('Valor Fixo inválido.', 'warning');
        }
        const currentPacotes = Array.isArray(formData.pacotesDePreco) ? formData.pacotesDePreco : [];
        if (formData.tipoDePrecificacao === 'Por Pacote') {
            if (currentPacotes.length === 0) return setAlert('Adicione pacotes.', 'warning');
            for (const [index, pacote] of currentPacotes.entries()) {
                if (!pacote.nome?.trim() || !pacote.sessoes || Number(pacote.sessoes) < 1 || pacote.valor === '' || pacote.valor === null || Number(pacote.valor) < 0) {
                    return setAlert(`Pacote ${index + 1} inválido.`, 'warning');
                }
            }
        }
        if (formData.disponibilidadeGlobal.tipo === 'unidades_especificas' && formData.disponibilidadeGlobal.unidadesPermitidas.length === 0) {
            return setAlert('Selecione pelo menos uma unidade.', 'warning');
        }

        const dataToSubmit = {
            nome: formData.nome,
            descricao: formData.descricao,
            tipo: formData.tipo,
            itens: currentItems.map(({ itemName, ...itemRest }) => ({ 
                ...itemRest, 
                quantidade: Number(itemRest.quantidade) || 1, 
                valorUnitario: Number(itemRest.valorUnitario) || 0 
            })),
            tipoDePrecificacao: formData.tipoDePrecificacao,
            valorFixo: formData.tipoDePrecificacao === 'Fixo' ? Number(formData.valorFixo) : undefined,
            pacotesDePreco: formData.tipoDePrecificacao === 'Por Pacote' ? currentPacotes.map(p => ({
                _id: p._id,
                nome: p.nome,
                sessoes: Number(p.sessoes),
                valor: Number(p.valor),
                validadeMeses: Number(p.validadeMeses) || 12,
                regrasUtilizacao: {
                    sessoesSimultaneas: Number(p.regrasUtilizacao?.sessoesSimultaneas) || 1,
                    intervaloMinimoEntreSessoes: Number(p.regrasUtilizacao?.intervaloMinimoEntreSessoes) || 0,
                    permiteAgendamentoOnline: p.regrasUtilizacao?.permiteAgendamentoOnline ?? true
                },
                recorrencia: p.recorrencia || { ativa: false }
            })) : [],
            visivelParaCliente: formData.visivelParaCliente,
            precoVisivelParaCliente: formData.precoVisivelParaCliente,
            destaqueNaVitrine: formData.destaqueNaVitrine,
            ativo: formData.ativo,
            agendavel: formData.agendavel,
            imagensDaGaleria: (Array.isArray(formData.imagensDaGaleria) ? formData.imagensDaGaleria : []).map(img => img._id || img),
            imagens: (Array.isArray(formData.imagens) ? formData.imagens : []).map(img => img._id || img),
            imagemPrincipal: formData.imagemPrincipal,
            disponibilidadeGlobal: {
                tipo: formData.disponibilidadeGlobal.tipo,
                unidadesPermitidas: formData.disponibilidadeGlobal.tipo === 'unidades_especificas'
                    ? formData.disponibilidadeGlobal.unidadesPermitidas
                    : []
            },
            ofertasPorUnidade: formData.disponibilidadeGlobal.tipo === 'nenhuma_unidade'
                ? []
                : formData.ofertasPorUnidade.map(({ unidadeNome, ...ou }) => ({
                    unidade: ou.unidade,
                    disponivel: ou.disponivel,
                    precoEspecifico: ou.precoEspecifico === '' ? undefined : Number(ou.precoEspecifico),
                    estoqueEspecifico: Number(ou.estoqueEspecifico) || 0,
                    controlaEstoque: ou.controlaEstoque
                })),
        };
        
        if (formData.tipoDePrecificacao !== 'Por Pacote') dataToSubmit.pacotesDePreco = [];
        if (formData.tipoDePrecificacao !== 'Fixo') delete dataToSubmit.valorFixo;

        const mutationOptions = { 
            onSuccess: onSaveSuccess, 
            onError: (err) => {
                console.error("❌ [ERRO_SUBMIT]", err);
                setAlert(err.response?.data?.error || 'Erro ao salvar oferta', 'danger');
            } 
        };

        if (isEditing) {
            updateOferta.mutate({ id: current._id, ofertaData: dataToSubmit }, mutationOptions);
        } else {
            createOferta.mutate(dataToSubmit, mutationOptions);
        }
    }, [formData, isEditing, current, createOferta, updateOferta, onSaveSuccess, setAlert]);

    const handleImageError = (e) => {
        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9Ijc1IiB5PSI3NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOWNhM2FmIj5TZW0gSW1hZ2VtPC90ZXh0Pjwvc3ZnPg==';
    };

    return (
        <form onSubmit={onSubmit} className="oferta-form" noValidate>
            <div className="form-header">
                <h3 className="form-title">
                    {isEditing ? `Editar Oferta` : 'Criar Nova Oferta'}
                </h3>
                <button type="button" onClick={onCloseForm} title="Fechar" className="form-close-button">
                    <XCircle size={24} />
                </button>
            </div>

            <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

            <div className="form-tabs-content">

                {STEP_IDS[currentStep - 1] === 'principal' && (
                    <fieldset className="form-section">
                        <legend className="form-section-title">Informações Principais</legend>
                        <div className="form-grid-container">
                            <div className="form-group full-width">
                                <label htmlFor="oferta-nome">Nome da Oferta *</label>
                                <input type="text" id="oferta-nome" name="nome" value={formData.nome} onChange={onChange} required className="form-input" maxLength={150}/>
                            </div>
                            <div className="form-group full-width">
                                <label htmlFor="oferta-descricao">Descrição</label>
                                <textarea id="oferta-descricao" name="descricao" value={formData.descricao} onChange={onChange} rows="3" className="form-input" maxLength={1000}></textarea>
                            </div>
                            <div className="form-group">
                                <label htmlFor="oferta-tipo">Tipo da Oferta *</label>
                                <select id="oferta-tipo" name="tipo" value={formData.tipo} onChange={onChange} required className="form-input" disabled={isEditing}>
                                    <option value="Individual">Individual</option>
                                    <option value="Pacote">Pacote</option>
                                    <option value="Combo">Combo</option>
                                </select>
                            </div>
                             <div className="form-group">
                                <label>Imagem Principal</label>
                                <div className="flex items-center gap-4 mt-2">
                                    <img 
                                        src={imagemPreview} 
                                        alt="Preview" 
                                        className="w-24 h-24 rounded-lg object-cover border"
                                        onError={handleImageError}
                                    />
                                    <div>
                                        <p className="form-hint">Gerencie a imagem principal e a galeria na aba "Imagens".</p>
                                    </div>
                                </div>
                             </div>
                        </div>
                    </fieldset>
                )}

                {STEP_IDS[currentStep - 1] === 'itens' && (
                    <fieldset className="form-section">
                        <legend className="form-section-title">Itens Incluídos na Oferta</legend>
                        {isEditing && (
                            <div className="panel-state-wrapper panel-info" style={{backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#b45309', marginBottom: '1rem'}}>
                                <AlertCircle size={18} className="icon"/>
                                <p><strong>Atenção:</strong> A alteração de itens, tipo e precificação é bloqueada após a criação se a oferta já estiver em uso por clientes.</p>
                            </div>
                        )}
                        {!isEditing && limiteIndividualAtingido && (
                            <div className="panel-state-wrapper panel-info" style={{backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#b45309', marginBottom: '1rem'}}>
                                <AlertCircle size={18} className="icon"/>
                                <p><strong>Oferta "Individual" permite só um item.</strong> Pra combinar vários itens num preço só, volte ao passo "Principal" e mude o tipo pra "Combo".</p>
                            </div>
                        )}

                        {!isEditing && !limiteIndividualAtingido && (
                        <div className="add-item-subform panel">
                             <h5>Adicionar Item</h5>
                             <div className="form-grid--auto-fit" style={{gap: '0.75rem 1rem'}}>
                                <div className="form-group">
                                    <label htmlFor="busca-item-tipo">Tipo</label>
                                    <select 
                                        id="busca-item-tipo" 
                                        value={buscaItemTipo} 
                                        onChange={(e) => { 
                                            setBuscaItemTipo(e.target.value); 
                                            setItemSelecionadoId('');
                                        }} 
                                        className="form-input" 
                                        disabled={isEditing}
                                    >
                                        <option value="Servico">Serviço</option>
                                        <option value="Recurso">Recurso</option>
                                        <option value="ItemVenda">Produto</option>
                                    </select>
                                </div>
                                
                                <div className="form-group" style={{gridColumn: 'span 2 / span 2', minWidth: '300px'}}>
                                    <label htmlFor="searchable-select-item">
                                        Buscar {buscaItemTipo} 
                                        {isBuscandoItens && ' (Carregando...)'}
                                    </label>
                                    
                                    <SearchableSelect
                                        options={opcoesParaBusca}
                                        value={itemSelecionadoId}
                                        onChange={handleItemSelecionado}
                                        name="searchable-select-item"
                                        placeholder={
                                            isBuscandoItens 
                                                ? `Carregando ${buscaItemTipo.toLowerCase()}s...` 
                                                : opcoesParaBusca.length > 0 
                                                    ? `Selecione um ${buscaItemTipo.toLowerCase()}...`
                                                    : `Nenhum ${buscaItemTipo.toLowerCase()} disponível`
                                        }
                                        onAddNew={null}
                                    />
                                    <p className="form-hint">
                                        {opcoesParaBusca.length} {buscaItemTipo.toLowerCase()}(s) disponível(is)
                                        {itemSelecionado && ` - Selecionado: ${itemSelecionado.nome}`}
                                    </p>
                                </div>

                                <button 
                                    type="button" 
                                    onClick={handleAdicionarItem} 
                                    disabled={!itemSelecionado || loading || isEditing} 
                                    className="action-button action-button-primary add-item-button"
                                >
                                    <Plus size={16}/> Adicionar
                                </button>
                             </div>
                        </div>
                        )}

                        <div className="dynamic-list-container mt-6">
                            <h5>Itens na Oferta ({Array.isArray(formData.itens) ? formData.itens.length : 0})</h5>
                             {(!Array.isArray(formData.itens) || formData.itens.length === 0) ? (
                                <div className="panel-state-wrapper panel-info">
                                    <Package size={32} className="icon"/>
                                    <p>Nenhum item adicionado.</p>
                                </div>
                             ) : (
                                <Fragment>
                                    <div className="dynamic-list-item" style={{fontWeight: 600, color: '#6b7280', fontSize: '0.8rem'}}>
                                        <div style={{flexBasis: '100px'}}>Tipo</div>
                                        <div className="form-group">Nome</div>
                                        <div style={{flexBasis: '100px', textAlign: 'right'}}>Valor Base</div>
                                        <div style={{width: '40px'}}></div>
                                    </div>
                                    {formData.itens.map((item, index) => (
                                        <div key={`${item.itemId}-${index}`} className="dynamic-list-item">
                                            <div style={{flexBasis: '100px'}}>
                                                <span className="item-type-badge">{item.tipo}</span>
                                            </div>
                                            <div className="form-group">
                                                <span className="item-name">{item.itemName || item.itemId}</span>
                                            </div>
                                            <div style={{flexBasis: '100px', textAlign: 'right'}}>
                                                <span className="item-base-price">{formatCurrency(item.valorUnitario, currency, locale)}</span>
                                            </div>
                                            <button 
                                                type="button" 
                                                onClick={() => handleRemoveItem(index)} 
                                                className="remove-item-button" 
                                                title="Remover Item" 
                                                disabled={isEditing}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </Fragment>
                             )}
                        </div>
                    </fieldset>
                )}

                {STEP_IDS[currentStep - 1] === 'precos' && (
                     <fieldset className="form-section">
                        <legend className="form-section-title">Configuração de Preços</legend>
                        {isEditing && (
                            <div className="panel-state-wrapper panel-info" style={{backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#b45309', marginBottom: '1rem'}}>
                                <AlertCircle size={18} className="icon"/>
                                <p><strong>Atenção:</strong> A alteração de itens, tipo e precificação é bloqueada após a criação se a oferta já estiver em uso por clientes.</p>
                            </div>
                        )}
                         <div className="form-group">
                            <label htmlFor="oferta-precificacao">Tipo de Precificação *</label>
                            <select id="oferta-precificacao" name="tipoDePrecificacao" value={formData.tipoDePrecificacao} onChange={onChange} required className="form-input" disabled={isEditing}>
                                <option value="Fixo">Valor Fixo Total</option>
                                <option value="Por Pacote">Por Pacote (Sessões)</option>
                                <option value="Sob Consulta">Sob Consulta</option>
                            </select>
                        </div>
                        {formData.tipoDePrecificacao === 'Fixo' && (
                            <div className="form-group">
                                <label htmlFor="oferta-valorFixo">Valor Fixo da Oferta (R$) *</label>
                                <input type="number" id="oferta-valorFixo" name="valorFixo" value={formData.valorFixo} onChange={onChange} required min="0" step="0.01" className="form-input" placeholder="0.00" disabled={isEditing}/>
                            </div>
                        )}
                        {formData.tipoDePrecificacao === 'Por Pacote' && (
                             <div className="dynamic-list-container panel">
                                <h5>Pacotes de Preço</h5>
                                {(Array.isArray(formData.pacotesDePreco) ? formData.pacotesDePreco : []).map((pacote, index) => (
                                    <div key={index} className="dynamic-list-item">
                                        <div className="form-group" style={{flexGrow: 1}}>
                                            <div className="form-grid--auto-fit" style={{gap: '0.5rem 1rem'}}>
                                                <div className="form-group" style={{minWidth: '150px'}}>
                                                    <label htmlFor={`pacote-nome-${index}`} className="text-xs">Nome *</label>
                                                    <input id={`pacote-nome-${index}`} type="text" value={pacote.nome} onChange={(e) => handlePacoteChange(index, 'nome', e.target.value)} required className="form-input" disabled={isEditing}/>
                                                </div>
                                                 <div className="form-group">
                                                     <label htmlFor={`pacote-sessoes-${index}`} className="text-xs">Sessões *</label>
                                                     <input id={`pacote-sessoes-${index}`} type="number" value={pacote.sessoes} onChange={(e) => handlePacoteChange(index, 'sessoes', e.target.value)} required min="1" className="form-input" disabled={isEditing}/>
                                                </div>
                                                 <div className="form-group">
                                                     <label htmlFor={`pacote-valor-${index}`} className="text-xs">Valor (R$) *</label>
                                                     <input id={`pacote-valor-${index}`} type="number" value={pacote.valor} onChange={(e) => handlePacoteChange(index, 'valor', e.target.value)} required min="0" step="0.01" className="form-input" placeholder="0.00" disabled={isEditing}/>
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor={`pacote-validade-${index}`} className="text-xs">Validade (meses) *</label>
                                                    <input id={`pacote-validade-${index}`} type="number" value={pacote.validadeMeses ?? 12} onChange={(e) => handlePacoteChange(index, 'validadeMeses', e.target.value)} required min="1" max="36" className="form-input" disabled={isEditing}/>
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor={`pacote-simultaneas-${index}`} className="text-xs">Máx. de sessões pendentes ao mesmo tempo</label>
                                                    <input id={`pacote-simultaneas-${index}`} type="number" value={pacote.regrasUtilizacao?.sessoesSimultaneas ?? 99} onChange={(e) => handlePacoteRegraChange(index, 'sessoesSimultaneas', e.target.value)} min="1" className="form-input" disabled={isEditing}/>
                                                    <p className="form-hint text-xs">Quantas sessões futuras (agendadas, ainda não realizadas) o cliente pode ter marcadas ao mesmo tempo. Use um valor baixo (ex.: 1) só se o cliente precisar concluir uma sessão antes de marcar a próxima.</p>
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor={`pacote-intervalo-${index}`} className="text-xs">Intervalo mín. entre sessões</label>
                                                    <div className="flex gap-2" style={{display: 'flex', gap: '0.5rem'}}>
                                                        <input
                                                            id={`pacote-intervalo-${index}`}
                                                            type="number"
                                                            value={
                                                                (intervaloUnidades[index] || 'horas') === 'dias'
                                                                    ? (pacote.regrasUtilizacao?.intervaloMinimoEntreSessoes ?? 0) / 24
                                                                    : (pacote.regrasUtilizacao?.intervaloMinimoEntreSessoes ?? 0)
                                                            }
                                                            onChange={(e) => handleIntervaloChange(index, e.target.value)}
                                                            min="0"
                                                            className="form-input"
                                                            disabled={isEditing}
                                                            style={{flex: 1}}
                                                        />
                                                        <select
                                                            id={`pacote-intervalo-unidade-${index}`}
                                                            value={intervaloUnidades[index] || 'horas'}
                                                            onChange={(e) => handleIntervaloUnidadeChange(index, e.target.value)}
                                                            className="form-input"
                                                            disabled={isEditing}
                                                            style={{flex: '0 0 auto', width: 'auto'}}
                                                        >
                                                            <option value="horas">Horas</option>
                                                            <option value="dias">Dias</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="form-group flex items-center gap-2" style={{paddingTop: '1.25rem'}}>
                                                    <input id={`pacote-online-${index}`} type="checkbox" checked={pacote.regrasUtilizacao?.permiteAgendamentoOnline ?? true} onChange={(e) => handlePacoteRegraChange(index, 'permiteAgendamentoOnline', e.target.checked)} disabled={isEditing}/>
                                                    <label htmlFor={`pacote-online-${index}`} className="text-xs">Permite agendamento online</label>
                                                </div>
                                            </div>
                                            <RecorrenciaFields
                                                idPrefix={`pacote-recorrencia-${index}`}
                                                value={pacote.recorrencia}
                                                onChange={(novaRecorrencia) => handlePacoteRecorrenciaChange(index, novaRecorrencia)}
                                            />
                                        </div>
                                        <button type="button" onClick={() => handleRemovePacote(index)} className="remove-item-button" title="Remover Pacote" disabled={isEditing}>
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                ))}
                                {(!Array.isArray(formData.pacotesDePreco) || formData.pacotesDePreco.length === 0) && <p className="text-center text-gray-500 my-2">Nenhum pacote definido.</p>}
                                <button type="button" onClick={handleAddPacote} className="add-item-button" disabled={isEditing}>
                                    <Plus size={16}/> Adicionar Opção de Pacote
                                </button>
                            </div>
                        )}
                    </fieldset>
                )}

                {STEP_IDS[currentStep - 1] === 'unidades' && (
                    <fieldset className="form-section">
                        <legend className="form-section-title">Disponibilidade por Unidade</legend>

                        <div className="form-group">
                            <label htmlFor="oferta-disponibilidade-tipo">Disponibilidade</label>
                            <select
                                id="oferta-disponibilidade-tipo"
                                value={formData.disponibilidadeGlobal.tipo}
                                onChange={handleDisponibilidadeTipoChange}
                                className="form-input"
                            >
                                <option value="todas_unidades">Todas as unidades</option>
                                <option value="unidades_especificas">Unidades específicas</option>
                                <option value="nenhuma_unidade">Nenhuma unidade (indisponível)</option>
                            </select>
                            <p className="form-hint">
                                {formData.disponibilidadeGlobal.tipo === 'todas_unidades' && 'A oferta ficará disponível em todas as unidades da conta.'}
                                {formData.disponibilidadeGlobal.tipo === 'unidades_especificas' && 'Selecione abaixo as unidades onde a oferta estará disponível.'}
                                {formData.disponibilidadeGlobal.tipo === 'nenhuma_unidade' && 'A oferta ficará indisponível em todas as unidades.'}
                            </p>
                        </div>

                        {formData.disponibilidadeGlobal.tipo !== 'nenhuma_unidade' && (
                            <div className="dynamic-list-container mt-6">
                                <h5>Configuração por Unidade {loadingUnidades && '(Carregando...)'}</h5>

                                {unidades.length === 0 && !loadingUnidades ? (
                                    <div className="panel-state-wrapper panel-info">
                                        <Building size={32} className="icon"/>
                                        <p>Nenhuma unidade cadastrada nesta conta.</p>
                                    </div>
                                ) : (
                                    <div className="dynamic-list-container panel">
                                        {unidades.map(unidade => {
                                            const unidadeId = unidade._id || unidade.id;
                                            const permitida = formData.disponibilidadeGlobal.tipo === 'todas_unidades'
                                                ? true
                                                : formData.disponibilidadeGlobal.unidadesPermitidas.includes(unidadeId);
                                            const config = getConfigUnidade(unidadeId);

                                            return (
                                                <div key={unidadeId} className="dynamic-list-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem' }}>
                                                    <div className="flex items-center gap-2 justify-between">
                                                        <label className="checkbox-label" style={{ margin: 0 }}>
                                                            {formData.disponibilidadeGlobal.tipo === 'unidades_especificas' && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={permitida}
                                                                    onChange={() => handleToggleUnidadePermitida(unidadeId)}
                                                                    className="checkbox-input"
                                                                />
                                                            )}
                                                            <span className="item-name" style={{ fontWeight: 600 }}>
                                                                {unidade.nome}{unidade.localidade ? ` - ${unidade.localidade}` : ''}{unidade.uf ? `/${unidade.uf}` : ''}
                                                            </span>
                                                        </label>
                                                    </div>

                                                    {permitida && (
                                                        <div className="form-grid--auto-fit" style={{ gap: '0.75rem 1rem' }}>
                                                            <div className="form-group checkbox-item" style={{ alignSelf: 'end' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    id={`unidade-disponivel-${unidadeId}`}
                                                                    checked={config.disponivel}
                                                                    onChange={(e) => handleUnidadeConfigChange(unidadeId, 'disponivel', e.target.checked)}
                                                                    className="form-checkbox"
                                                                />
                                                                <label htmlFor={`unidade-disponivel-${unidadeId}`}>Disponível</label>
                                                            </div>
                                                            <div className="form-group">
                                                                <label htmlFor={`unidade-preco-${unidadeId}`} className="text-xs">Preço Específico (R$)</label>
                                                                <input
                                                                    id={`unidade-preco-${unidadeId}`}
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    placeholder="Usar preço padrão"
                                                                    value={config.precoEspecifico}
                                                                    onChange={(e) => handleUnidadeConfigChange(unidadeId, 'precoEspecifico', e.target.value)}
                                                                    className="form-input"
                                                                />
                                                            </div>
                                                            <div className="form-group checkbox-item" style={{ alignSelf: 'end' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    id={`unidade-controla-estoque-${unidadeId}`}
                                                                    checked={config.controlaEstoque}
                                                                    onChange={(e) => handleUnidadeConfigChange(unidadeId, 'controlaEstoque', e.target.checked)}
                                                                    className="form-checkbox"
                                                                />
                                                                <label htmlFor={`unidade-controla-estoque-${unidadeId}`}>Controlar Estoque</label>
                                                            </div>
                                                            {config.controlaEstoque && (
                                                                <div className="form-group">
                                                                    <label htmlFor={`unidade-estoque-${unidadeId}`} className="text-xs">Estoque</label>
                                                                    <input
                                                                        id={`unidade-estoque-${unidadeId}`}
                                                                        type="number"
                                                                        min="0"
                                                                        step="1"
                                                                        value={config.estoqueEspecifico}
                                                                        onChange={(e) => handleUnidadeConfigChange(unidadeId, 'estoqueEspecifico', e.target.value)}
                                                                        className="form-input"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </fieldset>
                )}

                {STEP_IDS[currentStep - 1] === 'imagens' && (
                    <fieldset className="form-section">
                         <legend className="form-section-title">Imagens da Oferta</legend>
                         <OfertaImages 
                            oferta={current || { _id: formData._id, ...formData }}
                            onUpdate={handleImageUpdate}
                            isCreating={!isEditing}
                         />
                    </fieldset>
                )}

                 {STEP_IDS[currentStep - 1] === 'configuracoes' && (
                    <fieldset className="form-section">
                         <legend className="form-section-title">Configurações de Visibilidade e Agendamento</legend>
                         <div className="form-grid-container">
                            <div className="form-group checkbox-group vertical-group">
                                <label>Opções de Visibilidade</label>
                                <div className="checkbox-item">
                                    <input type="checkbox" id="oferta-ativo-config" name="ativo" checked={formData.ativo} onChange={onChange} className="form-checkbox"/>
                                    <label htmlFor="oferta-ativo-config">Oferta Ativa (Pode ser vendida/agendada)</label>
                                </div>
                                <div className="checkbox-item">
                                    <input type="checkbox" id="oferta-visivel-config" name="visivelParaCliente" checked={formData.visivelParaCliente} onChange={onChange} className="form-checkbox"/>
                                    <label htmlFor="oferta-visivel-config">Visível na Loja/App do Cliente</label>
                                </div>
                                <div className="checkbox-item">
                                    <input type="checkbox" id="oferta-preco-visivel-config" name="precoVisivelParaCliente" checked={formData.precoVisivelParaCliente} onChange={onChange} className="form-checkbox" disabled={formData.tipoDePrecificacao === 'Sob Consulta'}/>
                                    <label htmlFor="oferta-preco-visivel-config" className={formData.tipoDePrecificacao === 'Sob Consulta' ? 'text-gray-400' : ''}>Exibir Preço para o Cliente</label>
                                </div>
                                <div className="checkbox-item">
                                    <input type="checkbox" id="oferta-destaque-config" name="destaqueNaVitrine" checked={formData.destaqueNaVitrine} onChange={onChange} className="form-checkbox"/>
                                    <label htmlFor="oferta-destaque-config">Marcar como Destaque na Vitrine</label>
                                </div>
                            </div>
                            
                            <div className="form-group checkbox-group vertical-group">
                                 <label>Opções de Agendamento</label>
                                 <div className="checkbox-item">
                                    <input type="checkbox" id="oferta-agendavel-config" name="agendavel" checked={formData.agendavel} onChange={onChange} className="form-checkbox"/>
                                    <label htmlFor="oferta-agendavel-config">Requer Agendamento</label>
                                    <p className="form-hint">Marque se esta oferta/pacote consome tempo na agenda (ex: Pacote de Massagens).</p>
                                </div>
                            </div>
                         </div>
                    </fieldset>
                )}

            </div>

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary" disabled={loading}>
                    <XCircle size={18} className="icon"/> Cancelar
                </button>
                <FormStepNavigation
                    currentStep={currentStep}
                    totalSteps={STEP_LABELS.length}
                    onBack={() => setCurrentStep((s) => s - 1)}
                    onNext={() => setCurrentStep((s) => s + 1)}
                    isSubmitting={loading || isBuscandoItens}
                    submitLabel={isEditing ? 'Salvar Alterações' : 'Criar Oferta'}
                />
            </div>
        </form>
    );
};

OfertaForm.propTypes = {
    onCloseForm: PropTypes.func.isRequired,
    onSaveSuccess: PropTypes.func.isRequired,
    current: PropTypes.object
};

export default OfertaForm;